import { ApiClient } from "@modules/core/api/client/apiClient"
import { addRequestInterceptor, addResponseInterceptor } from "@modules/core/api/interceptors/apiInterceptor"
import { requestLoggerInterceptor } from "@modules/core/api/interceptors/requestLoggerInterceptor"
import { responseLoggerInterceptor } from "@modules/core/api/interceptors/responseLoggerInterceptor"
import { type StripeAvailableCountriesDto, StripeAvailableCountriesDtoSchema } from "@modules/payment/gateway/stripeDto"
import { StripeCountryCode, StripePlatform } from "@modules/payment/stripeTypes"
import axios, { type AxiosInstance } from "axios"

export class PaymentApi {
  private baseUrl = "stripe"
  private programBaseUrl = "programs"
  private axiosStripe: AxiosInstance

  constructor(private api: ApiClient) {
    this.axiosStripe = axios.create()
    addRequestInterceptor(this.axiosStripe, requestLoggerInterceptor())
    addResponseInterceptor(this.axiosStripe, responseLoggerInterceptor())
  }

  async createBoxStripeAccount(
    accountToken: string,
    countryCode: StripeCountryCode,
    stripePlatform: StripePlatform,
  ): Promise<{ url: string }> {
    const result = await this.api.post<{ url: string }>(`/box/${this.baseUrl}/create-account`, {
      accountToken,
      countryCode,
      bankAccountToken: null,
      businessUrl: "",
      stripePlatform,
    })
    return result.data
  }
  async createUserStripeAccount(
    accountToken: string,
    countryCode: StripeCountryCode,
    stripePlatform: StripePlatform,
  ): Promise<{ url: string }> {
    const result = await this.api.post<{ url: string }>(`${this.baseUrl}/create-account`, {
      accountToken,
      countryCode,
      bankAccountToken: null,
      businessUrl: "",
      stripePlatform,
    })
    return result.data
  }

  async createAccountToken(stripePlatform: StripePlatform) {
    const urlSearchParams = new URLSearchParams()
    urlSearchParams.append("account[tos_shown_and_accepted]", "true")

    const publicKey = await this.getStripePublicKey(stripePlatform)

    const result = await this.axiosStripe.post<{ id: string | undefined; error: { code: string } | undefined }>(
      "https://api.stripe.com/v1/tokens",
      urlSearchParams,
      {
        headers: {
          Authorization: `Bearer ${publicKey}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      },
    )

    return result.data
  }

  async getUpdateBoxAccountLink() {
    const result = await this.api.post<{ url: string }>(`/box/${this.baseUrl}/update-account-link`)
    return result.data
  }

  async getUpdateUserAccountLink() {
    const result = await this.api.post<{ url: string }>(`${this.baseUrl}/update-account-link`)
    return result.data
  }

  async createProgramPaymentSubscription(programId: string, returnUrl: string | null) {
    const result = await this.api.post<{
      clientSecret: string
      coachStripeAccountId: string
      url: string
      coachStripePlatform: StripePlatform
    }>(`${this.programBaseUrl}/${programId}/payment/checkout`, {
      embedded: true,
      returnUrl,
    })
    return result.data
  }

  async createProgramBundlePaymentSubscription(programBundleId: string, returnUrl: string | null) {
    const result = await this.api.post<{
      clientSecret: string
      coachStripeAccountId: string
      url: string
      coachStripePlatform: StripePlatform
    }>(`${this.baseUrl}/create-program-bundle-checkout-session`, {
      programBundleId,
      embedded: true,
      returnUrl,
    })
    return result.data
  }

  async createBoxProProgramPaymentSubscription(programId: string, returnUrl: string | null) {
    const result = await this.api.post<{
      clientSecret: string
      coachStripeAccountId: string
      url: string
      coachStripePlatform: StripePlatform
    }>(`${this.programBaseUrl}/box/${programId}/payment/checkout`, {
      programId,
      mode: {
        uiMode: "CUSTOM",
        returnUrl,
      },
    })
    return result.data
  }

  async getStripeAvailableCountries() {
    const result = await this.api.get<StripeAvailableCountriesDto>(`${this.baseUrl}/available-countries`)
    const parsedResult = StripeAvailableCountriesDtoSchema.safeParse(result.data)
    if (!parsedResult.success) {
      throw new Error("Invalid response from API: " + parsedResult.error.message)
    }
    return parsedResult.data
  }

  async getStripePublicKey(stripePlatform: StripePlatform) {
    const result = await this.api.get<string>(`${this.baseUrl}/publishable-key`, {
      params: {
        stripePlatform,
      },
    })
    return result.data
  }
}
